import { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Typography,
} from "@mui/material";
import { UserRepository } from "@amityco/ts-sdk";
import { AmityUiKitChat } from "@amityco/ui-kit-open-source";
import { useSelector } from "react-redux";
import { RootState } from "../../state";
import UserService from "../../services/userService";
import { User } from "../../entities/User";
import defaultAvatarImg from "../../img/defaultProfile.jpg";
import ImageBubble from "../../img/icons/messageBubble.png";
import UserName from "../../components/UserName/UserName";
import CommunityService from "../../services/communityService";
import DialogService from "../../components/Dialog/dialogService";
import AmityProvider from "../../components/Bootstrap/AmityProvider";

interface FollowersProps {
  user: User;
}

const Followers = ({ user }: FollowersProps) => {
  const [followStatus, setFollowStatus] = useState<
    Amity.FollowStatus[] | undefined
  >(undefined);
  const [followers, setFollowers] = useState<Array<User> | undefined>(
    undefined
  );

  const { getUserDetails } = UserService();
  const { createChannel, follow, sendMessage, unfollow } = CommunityService();
  const { open } = DialogService();

  const loginData = useSelector((state: RootState) => state.login);

  // Variables to handle pagination
  let nextPageFn: (() => void) | undefined; // Function to load the next page of data
  let hasMore: boolean | undefined; // Boolean flag indicating if more pages are available

  /*
   * Possible params for getFollowers:
   * userId: 'userId';
   * status?: 'all' | 'pending' | 'accepted';
   * limit?: number;
   */
  const sendNewMessage = (userTarget: User) => {
    //open("Send New Message", <NewMessage isOffer={false} targetUser={user} />, [])
    createChannel(
      String(userTarget.userId),
      loginData.userName + " and " + userTarget.userName
    )
      .then(function (data) {
        open(
          "Send New Message",
          <AmityProvider>
            <AmityUiKitChat defaultChannelId={data.channelId} />
          </AmityProvider>,
          []
        );
      })
      .catch(function (errorData) {
        console.log(errorData);
      });
  };

  const fetchAllDetails = async (
    followers: Amity.FollowStatus[]
  ): Promise<any[]> => {
    const requests = followers.map((f) => getUserDetails(parseInt(f.from)));
    const results = await Promise.all(requests);
    return results;
  };

  useEffect(() => {
    if (user?.userId) {
      UserRepository.Relationship.getFollowers(
        { userId: String(user.userId) },
        ({ data: followers, onNextPage, hasNextPage, loading, error }) => {
          if (error) {
            // Handle any errors that occur during retrieving data
          }
          if (loading) {
            // Handle the loading state, e.g., show a loading spinner
          }
          if (followers) {
            // Process the data
            setFollowStatus(followers);
          }
          hasMore = hasNextPage; // Update the hasMore flag
          nextPageFn = onNextPage; // Update the nextPage function with the new one provided
        }
      );
    }
  }, [user]);

  useEffect(() => {
    if (followStatus) {
      fetchAllDetails(followStatus).then((results) => {
        setFollowers(results);
      });
    }
  }, [followStatus]);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        rowGap: 2,
        marginBottom: "2.5rem",
      }}
    >
      {followers ? (
        followers.length > 0 ? (
          followers.map((item) => {
            const labelId = item?.userId
              ? `checkbox-list-secondary-label-${item.userId}`
              : `checkbox-list-secondary-label`;
            return (
              item?.userId && (
                <Box
                  key={`follower-${item.userId}`}
                  sx={{
                    width: { xs: "100%", sm: "calc(50% - 0.5rem)" },
                    paddingTop: "10px !important",
                    paddingBottom: "10px",
                    borderRadius: "10px",
                    backgroundColor: "white !important",
                    minHeight: "50px",
                    paddingLeft: "16px",
                    boxShadow: 0,
                  }}
                >
                  <ListItem
                    key={item.userId}
                    disablePadding
                    sx={{ width: "100%", padding: "4px 0px 4px 9px" }}
                    secondaryAction={
                      <IconButton
                        onClick={() => sendNewMessage(item)}
                        sx={{
                          borderRadius: "20px",
                          boxShadow: 0,
                          width: "30px",
                        }}
                      >
                        <img
                          loading="lazy"
                          width="20"
                          src={ImageBubble}
                          alt=""
                        />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar
                        alt={`Profile Image of ${item.userName}`}
                        src={
                          item.profileImage?.url
                            ? item.profileImage.url
                            : defaultAvatarImg
                        }
                        sx={{ width: 72, height: 72 }}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      sx={{ paddingLeft: "15px" }}
                      id={labelId}
                      primary={
                        <UserName
                          user={item}
                          variant={"followCardUserName"}
                          justifyContent={"start"}
                        />
                      }
                      secondary={
                        <Typography
                          variant="body1"
                          sx={{ color: "#4663ff !important" }}
                        >
                          {item.item_count} shirts
                        </Typography>
                      }
                    />
                  </ListItem>
                </Box>
              )
            );
          })
        ) : (
          <Box sx={{ height: { xs: "200px", md: "200px" } }}>
            <Typography
              sx={{ fontSize: { xs: "14px", md: "1rem" }, fontWeight: 700 }}
            >
              You don't have any followers yet, but once you start engaging with
              other collectors, sharing your unique shirts and contributing to
              the community, people will start following your journey. Keep
              collecting and showcasing your passion!
            </Typography>
          </Box>
        )
      ) : (
        <Skeleton variant="rectangular" height={100} animation="wave" />
      )}
    </Box>
  );
};

export default Followers;
